/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2023
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import _ from 'lodash';

import { sendTransactions } from '@ibm-watson/shared/modules/actions/api/transactions';
import { selectCustomDataTypeById } from '@ibm-watson/shared/modules/actions/store/data-types/selectors';
import { getById as selectEntityById } from '@ibm-watson/shared/modules/actions/store/entities/selectors';
import { addErrorMessage } from '@ibm-watson/shared/modules/ia/store/messages/actions';
import * as modelsOperations from '@ibm-watson/shared/modules/ia/store/operations';
import {
  getActionSkillId,
  getAssistantId
} from '@ibm-watson/shared/modules/ia/store/session/selectors';
import { ApiStatus } from '../../../../../ts';
import { DataType } from '../../../../../ts/action';
import { CreateAsyncThunk } from '../../../../../ts/state';
import { selectIsAccessUpdatesEnabled } from '../../../../ia/store/features/selectors';
import { selectIsNewIa } from '../../../../ia/store/instance/selectors';
import {
  buildOptionsList,
  DATA_TYPE_RESPONSE_TYPES
} from '../../../util/actionUtil';
import { entityAdapter } from '../adapter';

export const updateDataType = createAsyncThunk<
  DataType,
  {
    data_type: string;
    entity: string;
    modifiedOptions: any;
    responseType: string;
    values: any[];
    response: any;
  },
  CreateAsyncThunk
>(
  'data-types/update',
  async (params, { dispatch, getState }) => {
    try {
      const {
        data_type,
        entity: entityId,
        modifiedOptions,
        responseType = DATA_TYPE_RESPONSE_TYPES.option.id, //in future this will be text etc (for regex)
        values = []
      } = params;
      const state = getState();
      const skillId = getActionSkillId(state);
      const operations = [];
      // we will only update the values the user actually added/changed/deleted
      const updatedValues = [];
      values.forEach(value => {
        //due to the fact that store treats an entity update as a whole new set of values, we need to update entity
        //values one by one, (using the /values API [using transactions])
        if (!value.originalValue) {
          //the value does not have an 'originalValue', which implies it is a new value the user has added
          //we will create a new value with the 'value' supplied by the user
          operations.push({
            operation: 'create',
            parent_references: { entity: entityId },
            payload: value,
            target_reference: value.value,
            type: 'value'
          });
          //add 'originalValue' so we don't try to re-create the value the next time round
          updatedValues.push({ ...value, originalValue: value.value });
        } else {
          //this is an existing value (it has 'originalValue').
          //first check that it was not deleted
          if (
            modifiedOptions[value.originalValue] &&
            !modifiedOptions[value.originalValue].delete
          ) {
            if (value.originalValue !== value.value) {
              // Tanya has changed the value to be different that the originalValue
              // we must check to see if the new value matches the originalValue of any of the other values.
              // If 'value' matches the 'originalValue' of another value 'object' then the user has 'swapped' two values:
              // e.g. imagine the following
              // values: [
              //  { "value": "Boston", "synonyms": ["Big apple", "NYC"] }
              //  { "value": "New York", "synonyms": ["Beantown"] }
              // ]
              // To make it clear how this is stored in memory:
              // values: [
              //  { "value": "Boston", "synonyms": ["Big apple", "NYC"], originalValue: "Boston" }
              //  { "value": "New York", "synonyms": ["Beantown"], originalValue: "New York" }
              // ]
              // Tanya realizes there is an issue and renames these values so she ends up with
              // values: [
              //  { "value": "New York", "synonyms": ["Big apple", "NYC"], originalValue: "Boston" }
              //  { "value": "Boston", "synonyms": ["Beantown"], originalValue: "New York" }
              // ]
              // Note here, that the value that previously _was_ Boston is now renamed to New York, and vice versa
              // If we try to make value API calls to create this situation the first values api call will be
              // POST /entities/cities/values/Boston .... payload: { "value": "New York", "synonyms": ["Big apple", "NYC"] }
              // Store will reject the call as there is already another value called New York
              // The following code checks if there was previously a value that was "New York". If it finds such value
              // then the code sets the synonyms on that value to be the same as the synonyms that the value will have
              // after the update is made.. e.g.
              // initial state (prior to this code being invoked)
              // values: [
              //  { "value": "Boston", "synonyms": ["Big apple", "NYC"], originalValue: "Boston" }
              //  { "value": "New York", "synonyms": ["Beantown"], originalValue: "New York" }
              // ]
              // state after this code runs
              // values: [
              //  { "value": "Boston", "synonyms": ["Beantown"], originalValue: "Boston" }
              //  { "value": "New York", "synonyms": ["Big apple", "NYC"], originalValue: "New York" }
              // ]

              const tmp = _.find(values, { originalValue: value.value });
              if (tmp) {
                //we have found a value object that originally had the same 'value' as this newly updated value property
                operations.push({
                  operation: 'update',
                  parent_references: { entity: entityId },
                  payload: value,
                  target_reference: tmp.originalValue,
                  type: 'value'
                });
                updatedValues.push({
                  ...value,
                  originalValue: tmp.originalValue
                });
                return;
              }
            }
            // this is just a plain update, the user updated the value object (either by changing the value/synonyms)
            operations.push({
              operation: 'update',
              parent_references: { entity: entityId },
              payload: value,
              target_reference: value.originalValue,
              type: 'value'
            });
          }
          updatedValues.push({ ...value });
        }
      });
      //each key in the map represents the 'value' that was modified
      //in the entity.
      const modifiedValues = Object.keys(modifiedOptions);
      modifiedValues
        .filter(value => modifiedOptions[value].delete === true)
        .forEach(value => {
          //create a list of delete operations
          operations.push({
            operation: 'delete',
            parent_references: { entity: entityId },
            target_reference: value,
            type: 'value'
          });
        });
      operations.sort((a, b) => {
        //want to do create operations first, then update, then delete
        const { operation: opA } = a;
        const { operation: opB } = b;
        if (opA === opB) {
          return 0;
        }
        if (opA === 'create') {
          //create is always first
          return -1;
        }
        if (opB === 'create') {
          return 1;
        }
        if (opA === 'delete') {
          //delete is always last
          return -1;
        }
        if (opB === 'delete') {
          return 1;
        }
        //will never reach here
        return 0;
      });
      const oldEntity = selectEntityById(state, entityId);
      const oldDataType = selectCustomDataTypeById(state, params.data_type);
      const dataType = {
        ..._.pick(params, ['data_type', 'entity', 'title']),
        num_values: values?.length || 0
      };
      if (responseType === DATA_TYPE_RESPONSE_TYPES.option.id) {
        dataType.response = {
          response_type: responseType,
          options: buildOptionsList(values)
        };
      }
      const entityToUpdate = {
        entity: entityId,
        fuzz_match: true,
        values: updatedValues
      };
      operations.push({
        operation: 'update',
        payload: dataType,
        target_reference: data_type,
        type: 'data_type'
      });

      // Use revised API for Access Control improvements if enabled & isNewIA
      // TODO when accessUpdates flag is removed, replace useNewIAUpdatedAccess with simpler isNewIA on call to sendTransactions
      const isNewIA = selectIsNewIa(state);
      const useNewIAUpdatedAccess =
        isNewIA && selectIsAccessUpdatesEnabled(state);
      const assistantId = getAssistantId(state);

      await sendTransactions(
        skillId,
        {
          operations
        },
        useNewIAUpdatedAccess,
        assistantId
      );

      // update the corresponding entity in the redux.
      dispatch({
        type: '',
        payload: {
          operation: modelsOperations.UPDATE,
          response: {
            entities: {
              entities: {
                [entityId]: {
                  ...oldEntity,
                  ...entityToUpdate
                }
              }
            }
          }
        }
      });
      return {
        ...oldDataType,
        ...dataType
      };
    } catch (error) {
      dispatch(addErrorMessage(error));
      throw error;
    }
  },
  {
    condition: (_, { getState }) => {
      const { dataTypes } = getState();
      return dataTypes?.listStatus !== ApiStatus.LOADING;
    }
  }
);

export const saveDataType = createAction(
  'data_types/save-data_type',
  dataType => ({ payload: dataType })
);

export const removeDataType = createAction(
  'data_types/remove-data_type',
  dataTypeId => ({ payload: dataTypeId })
);

const reducers = {
  [updateDataType.pending.toString()]: (state, _action) => {
    state.updateStatus = ApiStatus.LOADING;
    state.updateError = null;
  },
  [updateDataType.fulfilled.toString()]: (state, { payload }) => {
    state.updateStatus = ApiStatus.SUCCEEDED;
    state.updateError = null;
    entityAdapter.setOne(state, payload);
  },
  [updateDataType.rejected.toString()]: (state, action) => {
    state.updateStatus = ApiStatus.ERROR;
    state.updateError = action.error;
  },
  [saveDataType.toString()]: (state, action) => {
    entityAdapter.upsertOne(state, action.payload);
  },
  [removeDataType.toString()]: (state, action) => {
    entityAdapter.removeOne(state, action.payload);
  }
};
export default reducers;
